/**
 * Popups slice of the English catalog.
 *
 * Split out of en.ts for the same reason as enBoards / enTiktok: en.ts is a
 * merge hotspot several branches append to at once, and it sits against the
 * 500-line fileSize ratchet. Composed back in en.ts, so `MessageKey` still
 * covers every key.
 *
 * Copy for the saved-popup management surface (audit 2026-08-05, D9) — before
 * it, the Popups page rendered only a template chooser and existing popups
 * were invisible and uneditable.
 */
export const enPopups = {
  'popups.yours.title': 'Your popups',
  'popups.yours.empty': 'No popups yet — pick a template below to create your first.',
  'popups.yours.edit': 'Edit',
  'popups.yours.delete': 'Delete',
  'popups.yours.deleteConfirm': 'Delete popup "{name}"? This cannot be undone.',
  'popups.yours.updated': 'Updated {date}',
  'popups.yours.loadFailed': 'Failed to load your popups',
  'popups.yours.deleted': 'Popup deleted',
  'popups.yours.deleteFailed': 'Failed to delete popup',
  'popups.yours.updatedToast': 'Popup updated',
  'popups.yours.createdToast': 'Popup saved successfully!',

  // Builder — the CTA destination. The server, the MCP draft tool and the
  // embed renderer have accepted `content.buttonLink` throughout; the editor
  // had no field for it, so on a popup with no form the button rendered with
  // no click handler at all.
  'popups.editor.buttonLink': 'Button link',
  'popups.editor.buttonLinkPlaceholder': 'https://yourstore.com/sale  or  /sale',
  'popups.editor.buttonLinkHelp': 'Where the button sends a visitor. Leave it empty and the button does nothing.',
  'popups.editor.buttonLinkUnused': 'This popup collects a form, so the button submits it — the link is kept but not used.',

  // Builder — binding the popup to one specific flow. `startFlowOnTrigger`
  // and `flowId` were accepted by the API and acted on by popupLeads from the
  // start; the builder fetched the flow list on every open and threw it away.
  'popups.editor.flowSection': 'Email flow',
  'popups.editor.flowLabel': 'Start this flow on submit',
  'popups.editor.flowNone': "Don't start a flow",
  'popups.editor.flowLoading': 'Loading flows…',
  'popups.editor.flowEmpty': 'No flows yet — build one under Flows, then come back.',
  'popups.editor.flowHelp': 'Runs for anyone who submits this popup, whatever the flow\'s own trigger says.',
  'popups.editor.flowAlreadyAutomatic': 'Flows triggered by "new subscriber" already run on every submit — this is for binding one that is not.',

  // Live / Paused. Before this the only way to stop a popup was Delete, and a
  // new one was always saved live (dogfood V34).
  'popups.status.live': 'Live',
  'popups.status.paused': 'Paused',
  'popups.status.toggleLabel': 'Popup {name} is {state}',
  'popups.status.confirmTitle': 'Make popup «{name}» live on your site?',
  'popups.status.confirmBody': 'Visitors who match its rules will start seeing it right away. You can pause it at any time.',
  'popups.status.confirm': 'Make it live',
  'popups.status.cancel': 'Cancel',
  'popups.status.nowLive': '«{name}» is live on your site',
  'popups.status.nowPaused': '«{name}» is paused — visitors no longer see it',
  'popups.status.failed': 'The status could not be changed. Try again.',
  'popups.status.unsaved': 'Takes effect when you save',
  'popups.editor.saveFailed': 'Failed to save popup. Please try again.',
} as const;
